/**
 * EmbedDialog — hands the reader a copy-paste iframe snippet for the chart
 * they are currently looking at.
 *
 * Shares the modal shell conventions of ExportDialog (overlay, header, footer,
 * shared `.button` classes) so the two dialogs read as one family.
 *
 * The snippet is an iframe rather than a script tag on purpose: the frame is
 * served from our origin, so the widget inside it talks to /api same-origin and
 * no embedder's domain ever has to be added to CORS_ALLOWED_ORIGINS.
 */

import { useState, useMemo, useRef, useEffect, type HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { encodeEmbedConfig, type EmbedChartKind, type EncodeEmbedInput } from '../utils/embedConfig';
import { getLang, useT } from '../i18n';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const DialogContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 12px 32px rgba(24, 28, 68, 0.18), 0 4px 8px rgba(0, 0, 0, 0.06);
  width: 560px;
  max-width: 92vw;
  max-height: 90vh;
  overflow: auto;
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
`;

const DialogTitle = styled.h3`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text-default);
  margin: 0;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 16px;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const DialogContent = styled.div`
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Intro = styled.p`
  margin: 0;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const Field = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
`;

const HeightInput = styled.input`
  width: 88px;
  padding: 6px 8px;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Checkbox = styled.input`
  accent-color: ${theme.colors.primary};
  cursor: pointer;
`;

const SnippetBox = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 132px;
  resize: vertical;
  padding: ${theme.spacing.sm};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: ${theme.colors.text.primary};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
`;

const CopiedNote = styled.span`
  margin-right: auto;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.primary};
`;

const ButtonBase = styled.div``;
interface DialogButtonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary';
}
const Button = ({ variant = 'secondary', className, ...rest }: DialogButtonProps) => (
  <ButtonBase
    {...rest}
    className={`button button--${variant === 'primary' ? 'primary' : 'outline'} button--size-m ${className ?? ''}`.trim()}
  />
);

const DEFAULT_HEIGHT: Record<EmbedChartKind, number> = {
  // The area chart's plot is 400px tall plus header, legend and footer; the bar
  // chart's is 300px. These land close to the natural height, so an embed looks
  // right even on a host page that never loads the resize helper.
  area: 560,
  bar: 460,
};

export interface EmbedDialogProps {
  chart: EmbedChartKind;
  /** The view to encode — the chart's current filters, drill-down and unit. */
  view: Omit<EncodeEmbedInput, 'chart'>;
  /** Human-readable chart title, used for the iframe's `title` attribute. */
  chartTitle: string;
  onClose: () => void;
}

// Where the embed page lives. In the browser the widget is always served from
// the same origin as the API it talks to, so deriving it from the running
// script's own location keeps dev, preview and prod correct without config.
function resolveEmbedOrigin(): string {
  if (typeof document !== 'undefined') {
    const script = document.querySelector<HTMLScriptElement>(
      'script[src*="budget-widget"]',
    );
    if (script?.src) {
      try {
        return new URL(script.src).origin;
      } catch {
        // fall through to the page origin
      }
    }
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

export function EmbedDialog({ chart, view, chartTitle, onClose }: EmbedDialogProps) {
  const m = useT();
  const [height, setHeight] = useState(DEFAULT_HEIGHT[chart]);
  // A bar chart is a single-year view, so pinning matters most there: pinned is
  // a stable citation, unpinned follows each new Haushalt.
  const [pinYear, setPinYear] = useState(chart === 'bar');
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const origin = useMemo(resolveEmbedOrigin, []);

  const snippet = useMemo(() => {
    const viewQuery = encodeEmbedConfig({
      ...view,
      chart,
      year: pinYear ? view.year : null,
    });
    // The UI language is a property of the page serving the frame, not of the
    // view: the embed route reads ?lang= and decodeEmbedConfig ignores it.
    // German is the default, so only English needs saying.
    const query = getLang() === 'en'
      ? (viewQuery ? `${viewQuery}&lang=en` : 'lang=en')
      : viewQuery;
    const url = `${origin}/embed/budget/${chart}${query ? `?${query}` : ''}`;
    // Escape the two characters that would break out of the title attribute.
    const safeTitle = chartTitle.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    // Deliberately just an iframe plus one optional script: resize.js finds the
    // frame by its window reference, so the embedder has no id to copy or keep
    // unique across several embeds on one page.
    return [
      `<iframe src="${url}"`,
      `        width="100%" height="${height}" style="border:0"`,
      `        title="${safeTitle}" loading="lazy"></iframe>`,
      `<script src="${origin}/embed/resize.js" async></script>`,
    ].join('\n');
  }, [origin, chart, view, pinYear, height, chartTitle]);

  // Escape closes, matching the rest of the widget's overlays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied (permissions, insecure context).
      // Select the text so Ctrl+C still works.
      textareaRef.current?.select();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <DialogContainer onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{m.embedDialog.title}</DialogTitle>
          <CloseButton onClick={onClose} title={m.common.close}>
            <span className="icon" data-icon="close" />
          </CloseButton>
        </DialogHeader>

        <DialogContent>
          <Intro>{m.embedDialog.intro}</Intro>

          <Row>
            <Field>
              {m.embedDialog.height}
              <HeightInput
                type="number"
                min={240}
                max={2000}
                step={20}
                value={height}
                onChange={e => setHeight(Number(e.target.value) || DEFAULT_HEIGHT[chart])}
              />
            </Field>
            {view.year !== null && (
              <Field>
                <Checkbox
                  type="checkbox"
                  checked={pinYear}
                  onChange={e => setPinYear(e.target.checked)}
                />
                {m.embedDialog.pinYear(view.year)}
              </Field>
            )}
          </Row>

          {!pinYear && view.year !== null && (
            <Hint>{m.embedDialog.unpinnedHint}</Hint>
          )}

          <SnippetBox ref={textareaRef} readOnly value={snippet} spellCheck={false} />

          <Hint>{m.embedDialog.resizeHint(height)}</Hint>
        </DialogContent>

        <DialogFooter>
          {copied && <CopiedNote>{m.embedDialog.copied}</CopiedNote>}
          <Button onClick={onClose}>{m.common.close}</Button>
          <Button variant="primary" onClick={handleCopy}>
            {m.embedDialog.copy}
          </Button>
        </DialogFooter>
      </DialogContainer>
    </Overlay>
  );
}
